import { ProjectsOverviewDemoEmptyPage } from "@components/common/empty-page/projects-overview-demo-empty-page";
import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { AuthenticatedProjectsOverview } from "@pages/projects/authenticated-projects-overview";
import { ProjectsTabActionButtons } from "@pages/projects/projects-tab-action-buttons";
import { UtilsTypes } from "@stellar/utils";
import { fetchingProjectsFlagsSelector, projectsSelector, selectedProjectSelector } from "@store/projects/projects-selector";
import { fetchProjects } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { isProjectDemo } from "@utils/project-utils";
import { OpenTabEvents, ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useMemo } from "react";
/**
 * Content in the projects page
 */
export function ProjectsPage({
  companyId,
  projectStatus
}) {
  const selectedProject = useAppSelector(selectedProjectSelector);
  const {
    isFetchingProjects
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const projects = useAppSelector(projectsSelector);
  const dispatch = useAppDispatch();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    projects: viewMode
  } = useAppSelector(dataViewModeSelector);
  const shouldShowDemoEmptyPage = useMemo(() => {
    if (!isFetchingProjects && projects.length === 1) {
      const project = projects[0];
      return (
        // Only show the "Demo empty page" screen if the user is in the active tab and the demo project is active
        projectStatus === UtilsTypes.ProjectArchivingState.active && isProjectDemo(project) && project.archivingState === "UNARCHIVED"
      );
    }
    return false;
  }, [isFetchingProjects, projectStatus, projects]);

  // Fetches the 1st page of both active and archived projects
  useEffect(() => {
    void dispatch(fetchProjects({
      companyId,
      projectArchivingState: UtilsTypes.ProjectArchivingState.active
    }));
    void dispatch(fetchProjects({
      companyId,
      projectArchivingState: UtilsTypes.ProjectArchivingState.archived
    }));
  }, [companyId, dispatch]);

  // useEffect to track what dataViewMode is being used when opening the projects tab
  useEffect(() => {
    trackEvent({
      name: ProjectEvents.openProjectsPage,
      props: {
        viewMode
      }
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to track this once when the component mounts
  }, []);
  if (shouldShowDemoEmptyPage) {
    return <ProjectsOverviewDemoEmptyPage demoProject={projects[0]} companyId={companyId} />;
  }
  return <TabsWithRoutes selectedTab={projectStatus} dataTestId="PROJECTS_CONTAINER" selectedProject={selectedProject} requiredAttribute={false} trackingEventPageName={OpenTabEvents.openProjectsTab} tabs={[{
    label: "Active",
    route: UtilsTypes.ProjectArchivingState.active,
    content: () => <AuthenticatedProjectsOverview companyId={companyId} projectArchivingState={UtilsTypes.ProjectArchivingState.active} shouldForceLoading={false} />,
    loadingContent: <AuthenticatedProjectsOverview companyId={companyId} projectArchivingState={UtilsTypes.ProjectArchivingState.active} shouldForceLoading={true} />,
    actionButtons: <ProjectsTabActionButtons />
  }, {
    label: "Archived",
    route: UtilsTypes.ProjectArchivingState.archived,
    content: () => <AuthenticatedProjectsOverview companyId={companyId} projectArchivingState={UtilsTypes.ProjectArchivingState.archived} shouldForceLoading={false} />,
    loadingContent: <AuthenticatedProjectsOverview companyId={companyId} projectArchivingState={UtilsTypes.ProjectArchivingState.archived} shouldForceLoading={true} />,
    actionButtons: <ProjectsTabActionButtons />
  }]} />;
}